import React, { useState, useRef } from 'react'
import { useAsync } from 'react-use'

import { Card, Tag, Dropdown, Image, Button, Space, Swiper, List, Grid, FloatingPanel, ProgressCircle, Dialog, Skeleton } from 'antd-mobile'
import { DownCircleOutlined } from '@ant-design/icons'

import { startPronunciationAssessment, stopPronunciationAssessment, textToSpeech } from '../../utils/speech'

import { imageList } from '../../const/images'

import speakerIcon from '../../assets/images/svg/speaker.svg'
import turtleIcon from '../../assets/images/svg/turtle.svg'
import microphoneIcon from '../../assets/images/svg/microphone.svg'
import stopIcon from '../../assets/images/svg/stop.svg'

const level = (score) => {
  return ((!score) || (score < 50)) ? '--adm-color-danger' : (score < 80) ? '--adm-color-warning' : '--adm-color-success'
}

let reco

const Form = () => {
  const ref = useRef(null)
  const [loading, setLoading] = useState(false)
  const [isRec, setIsRec] = useState(false)
  const [selectedItem, setSelectedItem] = useState()
  const [hintIndexes, setHintIndexes] = useState([])
  const [ind, setInd] = useState(0)
  const [score, setScore] = useState({})

  const { value } = useAsync(async () => {
    setSelectedItem(imageList[0])
    return imageList
  }, [])

  const handleRecord = async (rec) => {
    try {
      setLoading(true)
      if (!rec) {
        const re = await stopPronunciationAssessment(reco)
        if (re.detailedResult?.RecognitionStatus === 'Success') {
          setScore({ ...score, [ind]: re.accuracyScore })
        } else {
          Dialog.alert({
            content: `${re.detailedResult?.RecognitionStatus ?? 'Fail'}! Please do it again!`,
            closeOnMaskClick: true,
            confirmText: 'OK'
          })
        }
        setIsRec(rec)
      } else {
        reco = await startPronunciationAssessment(selectedItem.words[ind].word, selectedItem.lang)
        setIsRec(rec)
      }
      setLoading(false)
      return Promise.resolve()
    } catch (error) {
      setLoading(false)
      return Promise.reject(error)
    }
  }

  if (!value) return <Skeleton></Skeleton>
  return (
    <>
      <Card style={{ border: '1px solid #e1e1e1' }}>
        <Grid columns={24} gap={8}>
          <Grid.Item span={4}>
            <div style={{ marginTop: 10, fontWeight: 'bold', textDecoration: 'underline' }}>Topic</div>
          </Grid.Item>
          <Grid.Item span={20}>
            <Dropdown ref={ref}>
              <Dropdown.Item key='items' arrowIcon={<DownCircleOutlined style={{ fontSize: 16 }} />} title={selectedItem.title}>
                <List mode='card' style={{ margin: '0 16px' }}>
                  {(value.map((o, i) => (
                    <List.Item
                      key={i}
                      arrowIcon={false}
                      onClick={() => {
                        if (selectedItem.title !== o.title) {
                          setInd(0)
                          setSelectedItem(o)
                          setHintIndexes([])
                          setScore({})
                        }
                        ref.current?.close()
                      }}
                    >
                      {`${i + 1}. ${o.title}`}
                    </List.Item>
                  )))}
                </List>
              </Dropdown.Item>
            </Dropdown>
          </Grid.Item>
        </Grid>
      </Card>
      <Swiper
        onIndexChange={(i) => {
          setInd(i)
        }}
        indicator={(total, current) => (
          <div className='customIndicator'>
            {`${current + 1} / ${total}`}
          </div>
        )}
      >
        {(selectedItem.words.map((o, i) => (
          <Swiper.Item key={i}>
            <div style={{ margin: '16px 0' }}>
              <Image
                src={o.image}
                height='20vh'
                fit='contain'
                lazy
                style={{ borderRadius: 8 }}
              />
            </div>
            <Space justify='center' block>
              <Tag color={(hintIndexes.indexOf(i) !== -1) ? 'default' : 'primary'} style={{ padding: 8, fontSize: 20, borderRadius: 8 }} onClick={() => {
                setHintIndexes([...hintIndexes, i])
              }}>
                Hint ?
              </Tag>
            </Space>
            <div style={{ marginTop: 16 }}>
              <Card style={{ marginBottom: 40, border: '1px solid #e1e1e1', borderRadius: 12, boxShadow: '2px 2px 2px 1px #e1e1e1' }}>
                <Grid columns={24} gap={8}>
                  <Grid.Item span={10} style={{ marginTop: 12, fontWeight: 'bold' }}>
                    Your score:
                  </Grid.Item>
                  <Grid.Item span={14}>
                    <ProgressCircle
                      percent={score[ind] ?? 0}
                      style={{ '--size': '48px', '--fill-color': `var(${level(score[ind])})` }}
                    >
                      {score[ind] ?? 0}
                    </ProgressCircle>
                  </Grid.Item>
                </Grid>
                <div style={{ textAlign: 'center', height: 64 }}>
                  {(hintIndexes.indexOf(i) !== -1) && (
                    <>
                      <span style={{ fontWeight: 'bold', fontSize: 24, color: '#50514e' }}>
                        {o.word}
                      </span><br />
                      <span style={{ fontSize: 12, fontStyle: 'italic', color: '#9b9898' }}>
                        {o.pronounce}
                      </span><br />
                      <span style={{ color: '#8b8787' }}>
                        {o.translate}
                      </span>
                    </>
                  )}
                </div>
                <Space justify='center' block>
                  <Button fill='none' onClick={async () => await textToSpeech(o.word, selectedItem.lang)}><Image src={speakerIcon} height={16} fit='contain' /></Button>
                  <Button fill='none'><Image src={turtleIcon} height={16} fit='contain' /></Button>
                  <span style={{ fontSize: 20, color: '#50514e' }}>|</span>
                  <Button fill='none'><span style={{ fontSize: 14, color: '#50514e' }}>Your</span> <img src={speakerIcon} height={12} alt='speaker' /></Button>
                </Space>
              </Card>
            </div>
          </Swiper.Item>
        )))}
      </Swiper>
      <FloatingPanel anchors={[72, 72, 72]} handleDraggingOfContent={false} style={{ '--header-height': 0 }}>
        <Space justify='center' block style={{ margin: '4px 16px', padding: '4px', border: '1px solid #e1e1e1', borderRadius: 16 }}>
          <Button onClick={() => handleRecord(!isRec)} disabled={loading} style={{ backgroundColor: '#2e2e2e', borderRadius: 32 }}>
            <Image src={(isRec) ? stopIcon : microphoneIcon} height={32} width={32} fit='fill' />
          </Button>
        </Space>
      </FloatingPanel>
    </>
  )
}

export default Form
